import React, { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DoctorService from "./Discountcoupons.service";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import PropTypes from 'prop-types';
import { Col, CardBody, Card } from "reactstrap";
import PermissionAccess from '../../PermissionAccess/Access.js';

const EditDoctorModal = ({ showModalEditDoctor, handleToggleModalEditDoctor, id, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { authData } = PermissionAccess();

  const [data, setData] = useState({
    document_action: '',
    rejection_message: '',
    mmc_expiry_datetime: ''
  });

  const [errors, setErrors] = useState({
    document_action: '',
    rejection_message: '',
    mmc_expiry_datetime: ''
  });

  // Fetch doctor data on load
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await DoctorService.GetSingleDoctor(authData.sessionID, id);
        setLoading(false);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else if (response.data.status) {
          const doctorData = response.data.data.data;
          setData({
            document_action: doctorData.document_action === 'Pending' ? '' : doctorData.document_action, // Set to empty string if 'Pending'
            rejection_message: doctorData.document_msg || '',
            mmc_expiry_datetime: doctorData.mmc_expiry_datetime || '',
          });
        } else {
          Alerts.swalErrorAlert(t("Something went wrong, Please try again."));
        }
      } catch (error) {
        setLoading(false);
        const errContent = (error.response && error.response.data) || error.message || error.toString();
        Alerts.swalErrorAlert(errContent || t("Server Error, Please try again later."));
      }
    };
    if (id) fetchData();
  }, [id, authData.sessionID, navigate, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });

    if (name === 'document_action') {
      setErrors({ ...errors, document_action: '' });

      if (value !== 'Rejected') {
        setData(prevData => ({ ...prevData, rejection_message: '' }));
      }
    }

    if (name === 'rejection_message') {
      setData(prevData => ({ ...prevData, rejection_message: value }));
      if (value) {
        setErrors({ ...errors, rejection_message: '' });
      }
    }

    if (name === 'mmc_expiry_datetime') {
      setData(prevData => ({ ...prevData, mmc_expiry_datetime: value }));
      if (value) {
        setErrors({ ...errors, mmc_expiry_datetime: '' });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let validationErrors = {};

    if (!data.document_action) validationErrors.document_action = 'Document action is required';
    if (data.document_action === 'Rejected' && !data.rejection_message) {
      validationErrors.rejection_message = 'Rejection message is required';
    }
    if (data.document_action === 'Approved' && !data.mmc_expiry_datetime) {
      validationErrors.mmc_expiry_datetime = 'MMC expiry datetime is required for approval';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setLoading(true);
      try {
        console.log('Sending data to EditDoctor API:', data);
        const response = await DoctorService.EditDoctor(authData.sessionID, data, id);
        setLoading(false);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else if (response.data.status) {
          handleToggleModalEditDoctor();
          onSuccess();
          Alerts.swalSuccessAlert(t("Account status updated successfully."));
        } else {
          validationErrors.document_action = response.data.message || 'An error occurred';
          setErrors(validationErrors);
        }
      } catch (error) {
        setLoading(false);
        const errContent = (error.response && error.response.data) || error.message || error.toString();
        Alerts.swalErrorAlert(errContent || t("Server Error, Please try again later."));
      }
    }
  };

  const handleClose = () => {
    setErrors({
      document_action: '',
      rejection_message: '',
      mmc_expiry_datetime: '',
    });
    handleToggleModalEditDoctor();
  };

  return (
    <div
      className={showModalEditDoctor ? "modal d-block mt-4" : "modal"}
      style={{
        display: showModalEditDoctor ? 'block' : 'none',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}
      tabIndex="-1"
      role="dialog"
    >
      <form onSubmit={handleSubmit}>
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">{t("Account Status")}</h5>
            </div>
            <div className="modal-body">
              <Col md="12">
                <Card className="main-card mb-3">
                  <CardBody>
                    <LoadingOverlay
                      tag="div"
                      active={loading}
                      styles={{ overlay: (base) => ({ ...base }) }}
                      spinner={<DNALoader />}
                    >
                      <div className="form-group">
                        <label htmlFor="document-action" className="col-form-label">{t("Account Status")}:</label>
                        <select
                          className="form-control"
                          name="document_action"
                          onChange={handleChange}
                          value={data.document_action}
                        >
                          <option value="" disabled>{t("Select Status")}</option>
                          <option value="Approved">{t("Approved")}</option>
                          <option value="Rejected">{t("Rejected")}</option>
                        </select>
                        {errors.document_action && <strong className="error text-danger">{errors.document_action}</strong>}
                      </div>

                      {data.document_action === 'Approved' && (
                        <div className="form-group mt-3">
                          <label htmlFor="mmc-expiry-datetime" className="col-form-label">{t("MMC Registration Certificate Expiry")}:</label>
                          <input
                            type="datetime-local"
                            className="form-control"
                            name="mmc_expiry_datetime"
                            onChange={handleChange}
                            value={data.mmc_expiry_datetime}
                          />
                          {errors.mmc_expiry_datetime && <strong className="error text-danger">{errors.mmc_expiry_datetime}</strong>}
                        </div>
                      )}

                      {data.document_action === 'Rejected' && (
                        <div className="form-group mt-3">
                          <label htmlFor="rejection-message" className="col-form-label">{t("Rejection Message")}:</label>
                          <textarea
                            className="form-control"
                            name="rejection_message"
                            rows="3"
                            onChange={handleChange}
                            value={data.rejection_message}
                          ></textarea>
                          {errors.rejection_message && <strong className="error text-danger">{errors.rejection_message}</strong>}
                        </div>
                      )}

                      <div className="text-center mt-4">
                        <button type="button" className="btn btn-outline-light btn-hover-shine me-3" onClick={handleClose}>{t("Close")}</button>
                        <button type="submit" className="btn btn-custom-color">{t("Save changes")}</button>
                      </div>

                    </LoadingOverlay>
                  </CardBody>
                </Card>
              </Col>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

EditDoctorModal.propTypes = {
  showModalEditDoctor: PropTypes.bool.isRequired,
  handleToggleModalEditDoctor: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default EditDoctorModal;
